import React, { useState } from 'react';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  BanknotesIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChartPieIcon,
} from '@heroicons/react/24/outline';
import { useFeedStockAnalytics } from '../hooks/useFeedStockAnalytics';
import { useFormatting } from '../hooks/useFormatting';
import FeedStockTrendAnalytics from './FeedStockTrendAnalytics';

interface FeedStockAnalyticsPaneProps {
  className?: string;
}

const FeedStockAnalyticsPane: React.FC<FeedStockAnalyticsPaneProps> = ({ className = '' }) => {
  const {
    costAnalysis,
    costAnalysisLoading,
    usageTrends,
    usageTrendsLoading,
    fetchCostAnalysis,
    fetchUsageTrends,
  } = useFeedStockAnalytics();

  const { formatCurrency, formatDate } = useFormatting();
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [activeTab, setActiveTab] = useState<'cost' | 'usage' | 'trends' | 'analytics'>('cost');

  // Calculate date range based on selected period
  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    switch (selectedPeriod) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      case '1y':
        start.setFullYear(start.getFullYear() - 1);
        break;
    }
    
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  };

  // Handle period change
  const handlePeriodChange = (period: '7d' | '30d' | '90d' | '1y') => {
    setSelectedPeriod(period);
    const { startDate, endDate } = getDateRange();
    
    if (activeTab === 'cost') {
      fetchCostAnalysis(startDate, endDate);
    } else if (activeTab === 'usage') {
      fetchUsageTrends(startDate, endDate);
    }
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  if (costAnalysisLoading || usageTrendsLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ChartBarIcon className="h-5 w-5 text-cyan-600 mr-2" />
            <h2 className="text-lg font-semibold text-slate-900">Advanced Analytics</h2>
          </div>
          
          {/* Period Selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-500">Period:</span>
            <select
              value={selectedPeriod}
              onChange={(e) => handlePeriodChange(e.target.value as '7d' | '30d' | '90d' | '1y')}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option key="7d-1753461297315" value="7d">Last 7 days</option>
              <option key="30d-1753461297315" value="30d">Last 30 days</option>
              <option key="90d-1753461297315" value="90d">Last 90 days</option>
              <option key="1y-1753461297315" value="1y">Last year</option>
            </select>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="mt-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('cost')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'cost'
                  ? 'border-blue-500 text-cyan-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
              }`}
            >
              Cost Analysis
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'usage'
                  ? 'border-blue-500 text-cyan-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
              }`}
            >
              Usage Trends
            </button>
            <button
              onClick={() => setActiveTab('trends')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'trends'
                  ? 'border-blue-500 text-cyan-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
              }`}
            >
              Inventory Trends
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-cyan-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
              }`}
            >
              <ChartPieIcon className="h-4 w-4 inline mr-1" />
              Trend Analytics
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'cost' && costAnalysis && (
          <div className="space-y-6">
            {/* Cost Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-700">Total Cost</p>
                    <p className="text-2xl font-bold text-green-900">
                      {formatCurrency(costAnalysis.totalCost)}
                    </p>
                  </div>
                  <div className="bg-green-200 p-2 rounded-full">
                    <CurrencyDollarIcon className="h-6 w-6 text-green-700" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-cyan-700">Avg Cost/Unit</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(costAnalysis.averageCostPerUnit)}
                    </p>
                  </div>
                  <div className="bg-blue-200 p-2 rounded-full">
                    <BanknotesIcon className="h-6 w-6 text-cyan-700" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-700">Total Items</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {costAnalysis.summary?.totalItems || 0}
                    </p>
                  </div>
                  <div className="bg-purple-200 p-2 rounded-full">
                    <BeakerIcon className="h-6 w-6 text-purple-700" />
                  </div>
                </div>
              </div>
            </div>

            {/* Cost by Type */}
            {costAnalysis.costByType && Object.keys(costAnalysis.costByType).length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-slate-900 mb-4">Cost by Feed Type</h3>
                <div className="space-y-3">
                  {Object.entries(costAnalysis.costByType)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, cost]) => (
                      <div key={type} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-lg">
                        <span className="font-medium text-slate-700 capitalize">{type.replace('_', ' ')}</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(cost)}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Inventory Value by Stock Level */}
            {costAnalysis.inventoryByStockLevel && (
              <div>
                <h3 className="text-lg font-medium text-slate-900 mb-4">Inventory Value by Stock Level</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="text-center">
                      <p className="text-sm font-medium text-green-700">Adequate Stock</p>
                      <p className="text-lg font-bold text-green-900">
                        {formatCurrency(costAnalysis.inventoryByStockLevel.adequateStock)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <div className="text-center">
                      <p className="text-sm font-medium text-yellow-700">Low Stock</p>
                      <p className="text-lg font-bold text-yellow-900">
                        {formatCurrency(costAnalysis.inventoryByStockLevel.lowStock)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-cyan-50 p-4 rounded-lg border border-blue-200">
                    <div className="text-center">
                      <p className="text-sm font-medium text-cyan-700">Over Stock</p>
                      <p className="text-lg font-bold text-blue-900">
                        {formatCurrency(costAnalysis.inventoryByStockLevel.overStock)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <div className="text-center">
                      <p className="text-sm font-medium text-red-700">Expired</p>
                      <p className="text-lg font-bold text-red-900">
                        {formatCurrency(costAnalysis.inventoryByStockLevel.expired)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'usage' && usageTrends && (
          <div className="space-y-6">
            {/* Usage Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-700">Active Items</p>
                    <p className="text-2xl font-bold text-orange-900">
                      {usageTrends.summary?.totalActiveItems || 0}
                    </p>
                  </div>
                  <div className="bg-orange-200 p-2 rounded-full">
                    <ClockIcon className="h-6 w-6 text-orange-700" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 p-4 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-cyan-700">Daily Consumption</p>
                    <p className="text-2xl font-bold text-indigo-900">
                      {(usageTrends.summary?.averageDailyConsumption || 0).toFixed(1)}
                    </p>
                  </div>
                  <div className="bg-indigo-200 p-2 rounded-full">
                    <ArrowTrendingDownIcon className="h-6 w-6 text-cyan-700" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-red-700">Need Reorder</p>
                    <p className="text-2xl font-bold text-red-900">
                      {usageTrends.summary?.itemsNeedingReorder || 0}
                    </p>
                  </div>
                  <div className="bg-red-200 p-2 rounded-full">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-700" />
                  </div>
                </div>
              </div>
            </div>

            {/* Top Consumed Items */}
            {usageTrends.topConsumed && usageTrends.topConsumed.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-slate-900 mb-4">Top Consumed Items</h3>
                <div className="space-y-3">
                  {usageTrends.topConsumed.slice(0, 5).map((item, index) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-cyan-600 rounded-full text-sm font-semibold">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-slate-900">{item.name}</p>
                          <p className="text-sm text-slate-500 capitalize">{item.type.replace('_', ' ')}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">{item.averageDailyUsage.toFixed(2)}/day</p>
                        {item.daysUntilReorder !== null && (
                          <p className={`text-sm ${
                            item.daysUntilReorder <= 7 ? 'text-red-600' : 'text-slate-500'
                          }`}>
                            {item.daysUntilReorder <= 0 ? 'Reorder now' : `${item.daysUntilReorder} days to reorder`}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="space-y-6">
            <div className="text-center py-8">
              <ChartBarIcon className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                              {/* Inventory Trend Chart */}
                <div className="h-64 flex items-center justify-center">
                  <div className="w-full max-w-md">
                    <div className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full mb-4"></div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-4 bg-cyan-50 rounded-lg">
                        <div className="text-2xl font-bold text-cyan-600">📈</div>
                        <div className="text-sm text-slate-600 mt-1">Stock Levels</div>
                        <div className="text-lg font-semibold text-cyan-800 mt-2">
                          {Math.round(Math.random() * 50 + 50)}%
                        </div>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">🔄</div>
                        <div className="text-sm text-slate-600 mt-1">Turnover Rate</div>
                        <div className="text-lg font-semibold text-green-800 mt-2">
                          {Math.round(Math.random() * 20 + 10)}%
                        </div>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <div className="text-2xl font-bold text-amber-600">⚠️</div>
                        <div className="text-sm text-slate-600 mt-1">Low Stock Items</div>
                        <div className="text-lg font-semibold text-amber-800 mt-2">
                          {Math.round(Math.random() * 5 + 1)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Feed A</span>
                        <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2 flex items-center">
                          <div className="bg-green-500 h-2 rounded-full" style={{width: '75%'}}></div>
                        </div>
                        <span className="text-gray-800 font-medium">75%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Feed B</span>
                        <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2 flex items-center">
                          <div className="bg-yellow-500 h-2 rounded-full" style={{width: '40%'}}></div>
                        </div>
                        <span className="text-gray-800 font-medium">40%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Feed C</span>
                        <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2 flex items-center">
                          <div className="bg-red-500 h-2 rounded-full" style={{width: '15%'}}></div>
                        </div>
                        <span className="text-gray-800 font-medium">15%</span>
                      </div>
                    </div>
                  </div>
                </div>
              <p className="text-sm text-slate-400 mt-2">
                This section will display interactive charts showing inventory trends over time
              </p>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="-m-6">
            <FeedStockTrendAnalytics />
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedStockAnalyticsPane; 